import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import AnimatedModal from '../ui/AnimatedModal';
import FieldEditHistory from '../ui/FieldEditHistory';
import { resolveGoColorsDesignation } from '../../pages/screenings/goColorsDesignation';
import {
  PencilSquareIcon,
  BanknotesIcon,
  BriefcaseIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  UserCircleIcon,
  MapPinIcon,
  BuildingOffice2Icon,
  ClipboardDocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const SALARY_FIELD_LABELS = { designation: 'Designation', doj: 'Date of joining', grossSalary: 'Gross salary' };

// DOJ is stored as UTC midnight of the chosen day; the edit form and history work in YYYY-MM-DD.
const dojDay = (val) => {
  if (!val) return '';
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

const formatDay = (day) => {
  if (!day) return '';
  const d = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? day
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/** Go Colors: HR correcting filled details. Sends only the fields that changed. */
function SalaryEditForm({ data, defaultDesignation, onSave, onCancel }) {
  const initial = {
    designation: data?.designation && data.designation !== 'undefined' ? data.designation : '',
    doj: dojDay(data?.doj),
    grossSalary: data?.grossSalary || '',
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const changed = {};
    Object.keys(form).forEach((k) => {
      if (form[k].trim() !== initial[k]) changed[k] = form[k].trim();
    });
    if (!Object.keys(changed).length) {
      onCancel();
      return;
    }
    if (changed.grossSalary === '' || changed.doj === '') {
      toast.error('Date of Joining and Gross Salary cannot be empty');
      return;
    }
    setSaving(true);
    const ok = await onSave(changed);
    setSaving(false);
    if (ok) onCancel();
  };

  const inputClass =
    'mt-1 block w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500';
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="salary-edit-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Designation
          <input
            value={form.designation}
            onChange={set('designation')}
            maxLength={120}
            placeholder={defaultDesignation || 'Job title'}
            className={`${inputClass} normal-case tracking-normal font-normal`}
          />
        </label>
        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Date of Joining (DOJ)
          <input type="date" value={form.doj} onChange={set('doj')} required className={`${inputClass} font-normal`} />
        </label>
        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Gross Salary
          <input
            value={form.grossSalary}
            onChange={set('grossSalary')}
            maxLength={60}
            required
            className={`${inputClass} normal-case tracking-normal font-normal`}
          />
        </label>
      </div>
      <p className="text-[11px] text-slate-500">
        Leave Designation empty to use the job title. Your name and the time are saved in the edit history.
      </p>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={saving} className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-60">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

export default function SalaryDetailsModal({
  isOpen,
  onClose,
  candidate,
  salaryData: salaryDataProp,
  screeningId,
  screeningMeta,
  onRefresh,
  editable = false,
}) {
  const [resending, setResending] = useState(false);
  const [editing, setEditing] = useState(false);
  // The modal opens on a snapshot of the table's data; a saved edit replaces it with the server's copy.
  const [salaryData, setSalaryData] = useState(salaryDataProp);
  useEffect(() => {
    setSalaryData(salaryDataProp);
    setEditing(false);
  }, [salaryDataProp]);

  if (!isOpen || !candidate) return null;

  const candidateId = candidate.id || candidate._id;
  const isCompleted = salaryData?.status === 'completed';
  const isPending = salaryData?.status === 'pending';

  const handleResend = async () => {
    if (!screeningId || !candidateId) return;
    try {
      setResending(true);
      const res = await axios.post(
        `/api/arm-salary/${screeningId}/candidates/${candidateId}/send`
      );
      if (res.data.success) {
        toast.success(res.data.message || 'Email re-sent to Regional Head');
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error('Failed to re-send salary details email:', err);
      toast.error(err.response?.data?.message || 'Failed to re-send email');
    } finally {
      setResending(false);
    }
  };

  const handleSaveEdit = async (changed) => {
    try {
      const res = await axios.patch(`/api/arm-salary/${screeningId}/candidates/${candidateId}`, changed);
      if (res.data?.request) setSalaryData((prev) => ({ ...prev, ...res.data.request }));
      toast.success('Salary details updated');
      if (onRefresh) onRefresh();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save the change. Please try again.');
      return false;
    }
  };

  const handleCopyLink = () => {
    if (salaryData?.token) {
      const url = `${window.location.origin}/arm-salary/fill/${salaryData.token}`;
      navigator.clipboard.writeText(url);
      toast.success('Form link copied to clipboard!');
    }
  };

  const formatDate = (val) => {
    if (!val) return '—';
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      zIndex={10000}
      contentClassName="max-w-2xl w-full bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden mx-auto my-6"
    >
      <div className="p-6 bg-white">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100 shadow-2xs">
              <BanknotesIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">
                Salary &amp; Onboarding Details
              </h3>
              <p className="text-xs text-slate-500">
                Candidate: <span className="font-semibold text-slate-800">{candidate.name || 'Candidate'}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isCompleted ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-600" />
                Details Submitted
              </span>
            ) : isPending ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                <ClockIcon className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
                Awaiting Regional Head
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                Not Requested
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Salary Details Card (if filled) */}
        {isCompleted && (
          <div className="mt-5 p-4 rounded-xl bg-gradient-to-br from-emerald-50/50 via-white to-slate-50 border border-emerald-200/80 space-y-4">
            {editable && !editing && (
              <div className="flex justify-end -mb-2">
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  data-testid="salary-edit-btn"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                </button>
              </div>
            )}
            {editing ? (
              <SalaryEditForm
                data={salaryData}
                defaultDesignation={resolveGoColorsDesignation({}, screeningMeta)}
                onSave={handleSaveEdit}
                onCancel={() => setEditing(false)}
              />
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 bg-white rounded-lg border border-slate-100 shadow-2xs">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400 block mb-1 flex items-center gap-1">
                  <BriefcaseIcon className="w-3.5 h-3.5 text-slate-400" />
                  Designation
                </span>
                <span className="text-sm font-bold text-slate-800 block">
                  {resolveGoColorsDesignation(salaryData, screeningMeta) || '—'}
                </span>
              </div>

              <div className="p-3 bg-white rounded-lg border border-slate-100 shadow-2xs">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400 block mb-1 flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                  Date of Joining (DOJ)
                </span>
                <span className="text-sm font-bold text-slate-800 block">
                  {formatDate(salaryData?.doj)}
                </span>
              </div>

              <div className="p-3 bg-white rounded-lg border border-slate-100 shadow-2xs">
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400 block mb-1 flex items-center gap-1">
                  <BanknotesIcon className="w-3.5 h-3.5 text-emerald-600" />
                  Gross Salary
                </span>
                <span className="text-sm font-bold text-emerald-700 block">
                  {salaryData?.grossSalary || '—'}
                </span>
              </div>
            </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-emerald-100 text-xs text-slate-500">
              <span>
                Submitted to Regional Head / HRBP ({salaryData?.sentTo || 'Regional HRBP'})
              </span>
              <span>
                Completed
                {salaryData?.completedByName && (
                  <>
                    {' '}by <span className="font-semibold text-slate-700">{salaryData.completedByName}</span>
                  </>
                )}{' '}
                on {formatDate(salaryData?.completedAt)}
              </span>
            </div>
            <FieldEditHistory
              entries={salaryData?.editHistory}
              labels={SALARY_FIELD_LABELS}
              formatValue={(field, v) => (field === 'doj' ? formatDay(v) : v)}
            />
          </div>
        )}

        {/* Pending state details */}
        {isPending && (
          <div className="mt-5 p-4 rounded-xl bg-amber-50/60 border border-amber-200 text-sm space-y-3">
            <div className="flex items-start gap-2.5">
              <ClockIcon className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-amber-900">Email Sent to Regional Head</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  An email with a secure link was sent to{' '}
                  <span className="font-semibold text-amber-900">{salaryData?.sentTo || 'Regional HRBP'}</span> on{' '}
                  {formatDate(salaryData?.sentAt)}.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white rounded-lg text-xs font-semibold transition disabled:opacity-50"
              >
                <PaperAirplaneIcon className="w-3.5 h-3.5" />
                {resending ? 'Sending...' : 'Re-send Email'}
              </button>

              {salaryData?.token && (
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-lg text-xs font-semibold transition"
                >
                  <ClipboardDocumentIcon className="w-3.5 h-3.5 text-slate-400" />
                  Copy Link
                </button>
              )}
            </div>
          </div>
        )}

        {/* Candidate Context Snapshot */}
        <div className="mt-5 space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block">
            Candidate Snapshot
          </span>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs bg-slate-50 p-3.5 rounded-xl border border-slate-100">
            <div>
              <span className="text-slate-400 block">Store Code:</span>
              <span className="font-semibold text-slate-700">
                {candidate.storeId || candidate.storeDetails?.storeId || '—'}
              </span>
            </div>
            <div>
              <span className="text-slate-400 block">Store Name:</span>
              <span className="font-semibold text-slate-700">
                {candidate.storeDetails?.name || '—'}
              </span>
            </div>
            <div>
              <span className="text-slate-400 block">ARM Name:</span>
              <span className="font-semibold text-slate-700">
                {candidate.storeDetails?.armName || '—'}
              </span>
            </div>
            <div>
              <span className="text-slate-400 block">Region:</span>
              <span className="font-semibold text-slate-700">
                {candidate.storeDetails?.region || '—'}
              </span>
            </div>
            <div>
              <span className="text-slate-400 block">Interview Score:</span>
              <span className="font-semibold text-slate-700">
                {candidate.interviewScore != null ? `${candidate.interviewScore}%` : 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-slate-400 block">Phone:</span>
              <span className="font-semibold text-slate-700">
                {candidate.phone || '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Action button */}
        <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs rounded-xl transition"
          >
            Close
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
}
